import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import axios from 'lib/axiosConfig'
import { DeliveryAreaGroup } from 'types/model/common'
import { addDays, formatISO } from 'date-fns'
import {
  CollectionPointMutation,
  DeliveryMethod,
  DeliverySlotMutation,
  PickupSlotMutation,
} from 'types/api/serviceAreas'

export const useGetDeliverySlots = (
  placeId: string,
  deliveryGroupId: number,
  startsAt: string,
  endsAt: string
) => {
  const {
    isFetching,
    error,
    data: response,
  } = useQuery(
    ['getDeliverySlots', deliveryGroupId + startsAt + endsAt],
    async () => {
      return axios.get(
        `/v1/dts/places/${placeId}/delivery-area-groups/${deliveryGroupId}/delivery-slots`,
        { params: { starts_at: startsAt, ends_at: endsAt, raw_slots: true } }
      )
    },
    {
      keepPreviousData: true,
      enabled: Boolean(deliveryGroupId && placeId),
    }
  )
  const deliverySlots = !isFetching ? response?.data.data : []
  return { isFetching, error, deliverySlots }
}

export const useGetPickupSlots = (
  placeId: string,
  collectionPointId: number,
  startsAt: string,
  endsAt: string
) => {
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getPickupSlots', collectionPointId + startsAt + endsAt],
    async () => {
      return axios.get(
        `/v1/dts/places/${placeId}/pickup-locations/${collectionPointId}/pickup-slots`,
        { params: { starts_at: startsAt, ends_at: endsAt, raw_slots: true } }
      )
    },
    {
      keepPreviousData: true,
      enabled: Boolean(collectionPointId),
    }
  )

  const pickupSlots = !isLoading ? response?.data.data : []
  return { isLoading, error, pickupSlots }
}

export const useGetPickupSlot = (
  placeId: string,
  collectionPointId: number,
  pickupSlotId?: string
) => {
  const {
    isFetching,
    error,
    data: response,
  } = useQuery(
    ['getPickupSlot', collectionPointId, pickupSlotId],
    async () => {
      return axios.get(
        `/v1/dts/places/${placeId}/pickup-locations/${collectionPointId}/pickup-slots/${pickupSlotId}`
      )
    },
    {
      keepPreviousData: true,
      enabled: Boolean(collectionPointId && pickupSlotId),
    }
  )
  const pickupSlot = response?.data.data ? response?.data.data : []
  return { isFetching, error, pickupSlot }
}

export const useGetAllCoordinates = (
  allDeliveryAreas: DeliveryAreaGroup[],
  placeId: string
) => {
  const queries = useQueries(
    allDeliveryAreas.map((group) => ({
      queryKey: ['getCoordinates', group.id + placeId],
      queryFn: async () =>
        axios.get(
          `/v1/dts/places/${placeId}/delivery-area-groups/${group.id}/delivery-areas`
        ),
      enabled: Boolean(group.id && allDeliveryAreas),
    }))
  )
  const error = queries.some((q) => q.error)
  const isLoading = queries.some((q) => q.isLoading)
  const isFetching = queries.some((q) => q.isFetching)
  const coordinateGroups =
    !error && !isLoading
      ? queries.map((q) => q.data?.data.data).filter((q) => q.length > 0)
      : []
  return { coordinateGroups, isLoading, isFetching }
}

export const useGetDeliveryAreaGroups = (placeId: string) => {
  const {
    isLoading,
    isFetching,
    error,
    data: response,
  } = useQuery(
    ['getDeliveryAreaGroups', placeId],
    async () => axios.get(`/v1/dts/places/${placeId}/delivery-area-groups`),
    {
      keepPreviousData: true,
      enabled: placeId?.length > 0,
    }
  )
  const allDeliveryAreas =
    !isLoading && response?.data.data ? response?.data.data : []
  return { isLoading, isFetching, error, allDeliveryAreas }
}

export const useGetCollectionPoints = (placeId: string) => {
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(['getCollectionPoints', placeId], async () =>
    axios.get(`/v1/dts/places/${placeId}/pickup-locations`)
  )
  const collectionPoints =
    !isLoading && !error && response?.data.data ? response?.data.data : []
  return { isLoading, error, collectionPoints }
}

export const useGetDeliverySlot = (
  placeId: string,
  deliveryAreaGroupId: number,
  deliverySlotId?: string
) => {
  const {
    isFetching,
    error,
    data: response,
  } = useQuery(
    ['getPickupSlot', deliveryAreaGroupId + (deliverySlotId || '')],
    async () => {
      return axios.get(
        `/v1/dts/places/${placeId}/delivery-area-groups/${deliveryAreaGroupId}/delivery-slots/${deliverySlotId}`
      )
    },
    {
      keepPreviousData: true,
      enabled: Boolean(deliveryAreaGroupId && deliverySlotId),
    }
  )
  const deliverySlot = response?.data.data
  // eslint-disable-next-line prefer-spread
  return { isFetching, error, deliverySlot }
}

export const useCreateNewDeliverySlot = (
  deliveryGroupId: number,
  dateData: { startOfWeek: Date; date: Date }
) => {
  const queryClient = useQueryClient()
  return useMutation(
    (deliverySlotData: DeliverySlotMutation) =>
      axios.post(`/v1/dts/delivery-slots`, deliverySlotData),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliverySlots'])
      },
      onSuccess: () => {
        queryClient.refetchQueries([
          'getDeliverySlots',
          (((deliveryGroupId + formatISO(dateData.startOfWeek)) as string) +
            formatISO(addDays(dateData.startOfWeek, 6))) as string,
        ])
      },
    }
  )
}

export const useCreateNewPickupSlot = (
  collectionPointId: number,
  dateData: { startOfWeek: Date; date: Date }
) => {
  const startDate = formatISO(dateData.startOfWeek) as string
  const endDate = formatISO(addDays(dateData.startOfWeek, 6)) as string
  const queryClient = useQueryClient()
  return useMutation(
    (deliverySlotData: PickupSlotMutation) =>
      axios.post(`/v1/dts/pickup-slots`, deliverySlotData),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getPickupSlots'])
      },
      onSuccess: () => {
        queryClient.refetchQueries([
          'getPickupSlots',
          collectionPointId + startDate + endDate,
        ])
      },
    }
  )
}

export const useEditPickupSlot = (
  collectionPointId: string,
  dateData: { startOfWeek: Date; date: Date }
) => {
  const queryClient = useQueryClient()
  const startDate = formatISO(dateData.startOfWeek) as string
  const endDate = formatISO(addDays(dateData.startOfWeek, 6)) as string
  return useMutation(
    (deliverySlotData: PickupSlotMutation) =>
      axios.patch(`/v1/dts/pickup-slots`, deliverySlotData),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getPickupSlots'])
      },
      onSuccess: () => {
        queryClient.refetchQueries([
          'getPickupSlots',
          collectionPointId + startDate + endDate,
        ])
      },
    }
  )
}

export const useEditDeliverySlot = (
  deliveryGroupId: string,
  dateData: { startOfWeek: Date; date: Date }
) => {
  const queryClient = useQueryClient()
  const startDate = formatISO(dateData.startOfWeek) as string
  const endDate = formatISO(addDays(dateData.startOfWeek, 6)) as string
  return useMutation(
    (deliverySlotData: DeliverySlotMutation) =>
      axios.patch(`/v1/dts/delivery-slots`, deliverySlotData),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliverySlots'])
      },
      onSuccess: () => {
        queryClient.refetchQueries([
          'getDeliverySlots',
          deliveryGroupId + startDate + endDate,
        ])
      },
    }
  )
}

export const usePostNewAreaCoordinates = (
  placeId: string,
  id: number,
  copy?: boolean
) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: {
      delivery_areas: {
        area: number[][]
        label: string | null
      }[]
    }) =>
      axios.post(
        `/v1/dts/places/${placeId}/delivery-area-groups/${id}/delivery-areas`,
        values
      ),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliveryAreaGroups', placeId])
      },
      onSuccess: () => {
        if (!copy) {
          queryClient.fetchQuery(['getCoordinates', id + placeId])
          queryClient.fetchQuery(['getDeliveryAreaGroups', placeId])
        }
      },
    }
  )
}

export const usePatchNewAreaCoordinates = (placeId: string, id: number) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: {
      delivery_areas: {
        area: number[][]
        label: string | null
      }[]
    }) =>
      axios.patch(
        `/v1/dts/places/${placeId}/delivery-area-groups/${id}/delivery-areas`,
        values
      ),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliveryAreaGroups', placeId])
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['getCoordinates', id + placeId])
        queryClient.invalidateQueries(['getDeliveryAreaGroups', placeId])
      },
    }
  )
}

export const useDeleteArea = (
  placeId: string,
  deliveryAreaGroupId: number,
  areaId: number,
  refetchDeliveryAreas?: number[]
) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Record<string, never>) => {
      return axios.delete(
        `/v1/dts/places/${placeId}/delivery-area-groups/${deliveryAreaGroupId}/delivery-areas/${areaId}`,
        values
      )
    },
    {
      onMutate: async () => {
        queryClient.cancelQueries([
          'getCoordinates',
          deliveryAreaGroupId + placeId,
        ])
      },
      onSuccess: () => {
        queryClient.fetchQuery([
          'getCoordinates',
          deliveryAreaGroupId + placeId,
        ])
        queryClient.fetchQuery(['getDeliveryAreaGroups', placeId])
        refetchDeliveryAreas?.forEach((id) => {
          queryClient.fetchQuery(['getCoordinates', id + placeId])
        })
      },
    }
  )
}

export const useDeleteAreaGroup = (
  placeId: string,
  deliveryAreaGroupId: number
) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Record<string, never>) => {
      return axios.delete(
        `/v1/dts/places/${placeId}/delivery-area-groups/${deliveryAreaGroupId}`,
        values
      )
    },
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliveryAreaGroups', placeId])
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['getDeliveryAreaGroups', placeId])
      },
    }
  )
}

export const usePatchDeliveryAreaGroup = (
  placeId: string,
  deliveryAreaId: string
) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: { name: string }) =>
      axios.patch(
        `/v1/dts/places/${placeId}/delivery-area-groups/${deliveryAreaId}`,
        values
      ),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliveryAreaGroups', placeId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getDeliveryAreaGroups', placeId])
      },
    }
  )
}

export const usePostDeliveryAreaGroup = (placeId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: { name: string }) =>
      axios.post(`/v1/dts/places/${placeId}/delivery-area-groups`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliveryAreaGroups', placeId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getDeliveryAreaGroups', placeId])
      },
    }
  )
}

export const usePostCollectionPoint = (placeId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: CollectionPointMutation) =>
      axios.post(`/v1/dts/places/${placeId}/pickup-locations`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getCollectionPoints', placeId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getCollectionPoints', placeId])
      },
    }
  )
}

export const usePatchCollectionPoint = (
  placeId: string,
  pickupLocationId: number
) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: CollectionPointMutation) =>
      axios.patch(
        `/v1/dts/places/${placeId}/pickup-locations/${pickupLocationId}`,
        values
      ),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getCollectionPoints', placeId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getCollectionPoints', placeId])
      },
    }
  )
}

export const usePostDeliveryMethod = (placeId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (deliveryMethodData: DeliveryMethod) =>
      axios.post(`/v1/places/${placeId}/delivery-methods`, deliveryMethodData),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getDeliveryMethods', placeId])
      },
      onSuccess: () => {
        queryClient.refetchQueries(['getDeliveryMethods', placeId])
      },
    }
  )
}

export const useGetDeliveryMethods = (placeId: string) => {
  const {
    isFetching,
    error,
    data: response,
  } = useQuery(['getDeliveryMethods', placeId], async () => {
    return axios.get(`/v1/places/${placeId}/delivery-methods`)
  })
  const data = !isFetching ? response?.data.data : []
  const hasHomeDeliveryMethod =
    data.filter(
      (method: DeliveryMethod) =>
        method.details?.services?.[0].type === 'homedelivery'
    )?.length > 0
  return { isFetching, error, data, hasHomeDeliveryMethod }
}
